import { layout } from "@app/domain";
import type { WireCard } from "@app/protocol";
import { type CSSProperties, useRef, useState } from "react";
import { useCoarsePointer } from "../canvas/useCoarsePointer";
import type { AnyView } from "../variants/types";
import { send } from "../ws/singleton";
import { Card, cardLabel } from "./Card";
import { tapCard } from "./handTap";
import type { PassChoice } from "./passChoice";
import { useHandFocus } from "./useHandFocus";

const LIFTABLE =
  "focus-within:[translate:0_calc(-1*var(--lift))] hover:[translate:0_calc(-1*var(--lift))]";

type Hidden = ReadonlySet<string> | "all" | null;

/** A card still in the air during the deal. */
function isAway(hidden: Hidden, label: string): boolean {
  return hidden === "all" || (hidden?.has(label) ?? false);
}

function HandCard({
  card,
  place,
  size,
  on,
  dim,
  away,
  lifted,
  pressed,
  onPlay,
}: {
  card: WireCard;
  place: layout.HandPlace;
  size: { w: number; h: number };
  on: boolean;
  dim: boolean;
  away: boolean;
  lifted: boolean;
  pressed?: boolean | undefined;
  onPlay: (card: WireCard) => void;
}) {
  return (
    <div
      className={`absolute transition-[left,top,rotate,translate] duration-150 ease-out ${away ? "invisible" : ""} ${on ? LIFTABLE : ""} ${lifted ? "[translate:0_calc(-1*var(--lift))]" : ""}`}
      style={
        {
          left: place.x - size.w / 2,
          top: place.y - size.h / 2,
          rotate: `${place.rotate}deg`,
          zIndex: place.z,
          "--lift": `${size.h * layout.HAND_LIFT}px`,
        } as CSSProperties
      }
    >
      <Card
        card={card}
        width={size.w}
        height={size.h}
        state={on ? "on" : dim ? "off" : "plain"}
        onPlay={onPlay}
        pressable={dim}
        pressed={pressed}
      />
    </div>
  );
}

/**
 * Your cards where `layout.handPlaces` puts them: a flat row on phones, a two-row
 * fan on the wide canvas. Playable ones rise, the rest darken. `active` false
 * during the bid. `hidden` cards are still in flight (the deal).
 */
export function Hand({
  view,
  yourTurn,
  canvas,
  active = true,
  hidden = null,
  pass = null,
}: {
  view: AnyView;
  yourTurn: boolean;
  canvas: layout.Canvas;
  active?: boolean;
  hidden?: Hidden;
  /** Hearts: choosing the three cards to pass; every card is a toggle and the chosen stand up. */
  pass?: PassChoice | null;
}) {
  const validKeys = new Set((view.validCards ?? []).map(cardLabel));
  const play = (played: WireCard) => send({ type: "move", move: { kind: "play", card: played } });
  const cards = view.yourHand;
  const places = layout.handPlaces(canvas, cards.length);
  const dim = active && yourTurn;
  const twoStep = useCoarsePointer();
  const [lifted, setLifted] = useState<string | null>(null);
  // Nothing stands up while it is not your move.
  const standing = dim ? lifted : null;
  const fan = useRef<HTMLDivElement>(null);
  // A card still in the air is invisible and cannot take focus: wait for the deal to land.
  const dealing = cards.some((c) => isAway(hidden, cardLabel(c)));
  useHandFocus(fan, (dim || pass !== null) && !dealing);
  const tap = (played: WireCard, label: string) => {
    const next = tapCard(standing, label, { twoStep, playable: validKeys.has(label) });
    setLifted(next.lifted);
    if (next.play) play(played);
  };
  return (
    <div ref={fan} className="relative h-full">
      {cards.map((c, i) => {
        const label = cardLabel(c);
        return (
          <HandCard
            key={label}
            card={c}
            place={places[i] ?? { x: 0, y: 0, rotate: 0, z: i }}
            size={canvas.card}
            on={pass !== null || (dim && validKeys.has(label))}
            dim={pass === null && dim}
            away={isAway(hidden, label)}
            lifted={pass === null ? standing === label : pass.selected.has(label)}
            pressed={pass === null ? undefined : pass.selected.has(label)}
            onPlay={(played) => (pass === null ? tap(played, label) : pass.toggle(label))}
          />
        );
      })}
    </div>
  );
}
